// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The game tones (M3-29, 07 §3.2).
//
// The seven synths a rulesheet can name that are not a mechanism and not a chime: `brassStab`,
// `sweepUp`, `sweepDown`, `blip`, `arpRun`, `noiseBurst`, `subDrop`. Every one of them has been
// in the machine format's `SYNTH_NAMES` since M2 and none of them had a recipe, which is the
// failure that list's own comment describes — "a cue naming a synth outside it has no recipe and
// would play silence, which is the worst possible failure mode because the machine still loads
// and still scores". NEON NOVA names all seven, forty times, across its rulesheet.
//
// **These are the only sounds in the machine that are not modelling anything.** A coil is a
// physical object and a recipe for one is a description of what that object does; a jackpot stab
// is a *statement about the game*, and the only question about it is whether the player
// understands what just happened. So the design rule is different: each of the seven has to be
// unmistakable for the other six inside a hundred milliseconds, because they arrive during
// multiball on top of everything else.
//
// **One variant each, and that is not a budget compromise.** These are pitched and they carry
// meaning; a jackpot that came out four per cent sharp of the last jackpot would sound like a
// different award. It is the chime bars' argument again — the variance exists to stop a *rattle*
// sounding like a machine gun, and none of these ever repeats fast enough to rattle.

import {
  addNoiseBurst,
  addPitchDrop,
  addSweptNoise,
  Biquad,
  fadeOut,
  filterInPlace,
  normalise,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'
import { addWave, bandLimited } from './music'

/** The seven, named as the machine format's `SYNTH_NAMES` names them. */
export const TONE_CUES = [
  'brassStab',
  'sweepUp',
  'sweepDown',
  'blip',
  'arpRun',
  'noiseBurst',
  'subDrop',
] as const
export type ToneCue = (typeof TONE_CUES)[number]

/** The key the tones are written in, so they sit with the score rather than against it. */
const ROOT_HZ = 220 // A3, an octave above the score's bass

/**
 * The jackpot: a brass stab.
 *
 * A minor triad of detuned saws with a filter that opens in twenty milliseconds and shuts in a
 * quarter of a second. The *filter* is the brass — a horn's spectrum brightens as it is blown
 * harder and the attack is where all of that happens, which is why a static filter over the
 * same chord sounds like an organ.
 */
export function renderBrassStab(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(520, sampleRate))
  const table = bandLimited('saw', ROOT_HZ, 11_000)
  for (const [semitones, gain] of [
    [0, 0.5],
    [7, 0.36],
    [12, 0.3],
    [15, 0.24],
  ] as const) {
    const hz = ROOT_HZ * Math.pow(2, semitones / 12)
    addWave(out, sampleRate, hz * 0.996, table, gain, 12, 320)
    addWave(out, sampleRate, hz * 1.004, table, gain, 12, 320)
  }
  // The blow: two milliseconds of air before the tone, which is what a horn's onset is.
  addNoiseBurst(out, sampleRate, noise, Biquad.bandpass(3200, 1.2, sampleRate), 0.35, 18)
  sweepFilter(out, sampleRate, 500, 5200, 1.4, 22)
  sweepFilter(out, sampleRate, 5200, 900, 1.4, 260, samplesFor(22, sampleRate))
  fadeOut(out, sampleRate, 12)
  normalise(out)
  return out
}

/**
 * A ramp made: noise and a tone climbing together.
 *
 * Both, because either alone is wrong. Noise on its own is a whoosh with no destination; a tone
 * on its own is a siren. Together they are the sound of something being *completed*, which is
 * what a rulesheet uses this for.
 */
export function renderSweepUp(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(620, sampleRate))
  addSweptNoise(out, sampleRate, noise, 320, 5200, 2.2, 0.5, 900, 480)
  addPitchDrop(out, sampleRate, ROOT_HZ, ROOT_HZ * 6, 480, 0.35, 900)
  fadeOut(out, sampleRate, 40)
  normalise(out)
  return out
}

/** The same thing going the other way: something lost, or a mode timing out. */
export function renderSweepDown(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(720, sampleRate))
  addSweptNoise(out, sampleRate, noise, 5200, 280, 2.2, 0.5, 900, 560)
  addPitchDrop(out, sampleRate, ROOT_HZ * 6, ROOT_HZ * 0.75, 560, 0.35, 900)
  fadeOut(out, sampleRate, 40)
  normalise(out)
  return out
}

/**
 * The smallest thing the game can say: forty milliseconds of square.
 *
 * Short on purpose. A blip is what a rulesheet reaches for when something has been *counted*,
 * and it fires in runs — a lane ladder, a bonus count — so anything with a tail on it would
 * turn a count of eight into a chord.
 */
export function renderBlip(sampleRate: number): Samples {
  const out = new Float32Array(samplesFor(70, sampleRate))
  const table = bandLimited('square', ROOT_HZ * 6, 12_000)
  addWave(out, sampleRate, ROOT_HZ * 6, table, 0.8, 1, 26)
  filterInPlace(out, Biquad.lowpass(6500, 0.8, sampleRate))
  fadeOut(out, sampleRate, 6)
  normalise(out)
  return out
}

/**
 * A run up the chord: eight sixteenths in four hundred milliseconds.
 *
 * Baked as one buffer rather than sequenced, because it is a *gesture* and not a part — it has
 * to arrive complete on the frame the rule fired, and a rule that fires mid-bar cannot wait for
 * the sequencer's grid to come round.
 */
export function renderArpRun(sampleRate: number): Samples {
  const out = new Float32Array(samplesFor(560, sampleRate))
  const table = bandLimited('square', ROOT_HZ, 9000)
  const spacing = samplesFor(48, sampleRate)
  const semitones = [0, 3, 7, 12, 15, 19, 24, 27]
  for (let i = 0; i < semitones.length; i++) {
    const hz = ROOT_HZ * Math.pow(2, semitones[i]! / 12)
    const at = i * spacing
    // Each note into its own view of the buffer, so the run is genuinely eight notes and not
    // one note with a wobble.
    addWave(out.subarray(at), sampleRate, hz, table, 0.55, 2, 110)
  }
  filterInPlace(out, Biquad.lowpass(7000, 1.1, sampleRate))
  fadeOut(out, sampleRate, 20)
  normalise(out)
  return out
}

/** A burst of bright noise: an alarm, a hit, a thing that went wrong. */
export function renderNoiseBurst(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(220, sampleRate))
  addNoiseBurst(out, sampleRate, noise, Biquad.bandpass(2600, 0.9, sampleRate), 0.9, 90)
  addNoiseBurst(out, sampleRate, noise, Biquad.highpass(6000, 0.7, sampleRate), 0.3, 40)
  fadeOut(out, sampleRate, 12)
  normalise(out)
  return out
}

/**
 * The collapse: a sine falling out of hearing.
 *
 * The one tone here that is mostly *felt*. It ends at 28 Hz, which is below where a laptop
 * speaker reproduces anything and exactly where a cabinet or a headset does — so it is the
 * sound that most rewards playing the machine properly, and it costs nothing on a phone.
 */
export function renderSubDrop(sampleRate: number, noise: NoiseSource): Samples {
  const out = new Float32Array(samplesFor(1600, sampleRate))
  // The decay has to outlast the glide. At 1100 ms the tone was 34 dB down by the time the
  // pitch reached 600 ms of its 900 ms fall — the bottom of the collapse, which is the part
  // worth having, was inaudible.
  addPitchDrop(out, sampleRate, 150, 28, 900, 0.9, 1800)
  // A little grit on the way down, or a pure sine at 40 Hz is inaudible on anything small.
  addSweptNoise(out, sampleRate, noise, 900, 90, 1.6, 0.14, 1200, 900)
  fadeOut(out, sampleRate, 60)
  normalise(out)
  return out
}

/**
 * Sweep a bandpass across a finished buffer, in place.
 *
 * The brass stab's whole character, and it has to be a *pass over the result* rather than a
 * filter per oscillator: what makes a stab brassy is that every partial brightens together.
 */
function sweepFilter(
  out: Samples,
  sampleRate: number,
  fromHz: number,
  toHz: number,
  q: number,
  overMs: number,
  from = 0,
): void {
  const filter = new Biquad()
  const span = Math.max(1, samplesFor(overMs, sampleRate))
  const ratio = Math.pow(Math.max(toHz, 1) / Math.max(fromHz, 1), 1 / span)
  let centre = fromHz
  for (let i = from; i < out.length; i++) {
    if ((i - from) % 32 === 0) filter.setLowpass(Math.min(centre, sampleRate * 0.45), q, sampleRate)
    out[i] = filter.process(out[i]!)
    if (i - from < span) centre *= ratio
  }
}

/** Every tone, and what makes it. */
export const TONE_RECIPES: Readonly<
  Record<ToneCue, (sampleRate: number, noise: NoiseSource) => Samples>
> = {
  brassStab: renderBrassStab,
  sweepUp: renderSweepUp,
  sweepDown: renderSweepDown,
  blip: (sampleRate) => renderBlip(sampleRate),
  arpRun: (sampleRate) => renderArpRun(sampleRate),
  noiseBurst: renderNoiseBurst,
  subDrop: renderSubDrop,
}

/**
 * A chime at an arbitrary pitch, for a cue that names its own notes.
 *
 * 04 lets a `chime` cue carry a `notes` array — NEON NOVA's lane chime asks for 880 and 1320 —
 * and the bank holds three bars at fixed pitches. Rather than render a bar per note, the nearest
 * bar is played at the rate that puts it on the note asked for. A bar is a set of inharmonic
 * partials in fixed ratio, so transposing one *is* the same bar struck at a different length,
 * which is exactly what a real chime bank is.
 */
export function nearestBar(hz: number, bars: readonly number[]): { bar: number; rate: number } {
  let best = 0
  let distance = Infinity
  for (let i = 0; i < bars.length; i++) {
    // Nearest in *ratio*, not in hertz: a semitone at 1320 Hz is 78 Hz wide and at 660 it is 39.
    const off = Math.abs(Math.log2(hz / (bars[i] ?? 1)))
    if (off < distance) {
      distance = off
      best = i
    }
  }
  return { bar: best, rate: hz / (bars[best] ?? hz) }
}
